<script setup lang="ts">
const { offerExpirationDays } = useRuntimeConfig().public;
defineEmits(["close"]);
defineProps({
  success: {
    type: Boolean,
    default: false,
  },
  amount: {
    type: String,
    required: true,
  },
  isProcessing: {
    type: Boolean,
    required: false,
    default: false,
  },
  errorMessage: {
    type: String,
    required: false,
    default: null,
  },
});
const localePath = useLocalePath();
</script>
<template>
  <div>
    <div
      v-if="!isProcessing"
      class="flex flex-col gap-0.5 items-center max-w-xs mx-auto mt-9 mb-6">
      <NuxtIcon
        :name="
          success ? 'mdi:check-circle-outline' : 'mdi:cancel-circle-outline'
        "
        class="w-20 h-20"
        :class="success ? 'text-success-300' : 'text-error-300'" />
      <p
        class="mt-4 text-neutral-200 font-bold text-xl text-center"
        data-test="offer-submitted-successfully">
        {{
          success
            ? $t("main.modalOfferBegin.offerSent")
            : $t("main.offers.transactionFailed")
        }}
      </p>
      <i18n-t
        v-if="success"
        tag="p"
        class="text-neutral-300"
        keypath="main.modalOfferBegin.offerSentInfo"
        scope="global">
        <template #amount>
          <span class="font-bold text-neutral-200">
            {{
              $t("main.modalOfferBegin.offerSentInfoAmount", {
                amount: roundNum(Number(amount)),
              })
            }}</span
          >
        </template>
      </i18n-t>
      <i18n-t
        v-if="success"
        tag="p"
        class="text-neutral-300 text-center"
        keypath="main.modalOfferBegin.offerSentExpiration"
        scope="global">
        <template #date>
          <span class="font-bold text-neutral-200">
            {{
              new Date(
                new Date().getTime() + offerExpirationDays * 24 * 60 * 60 * 1000
              ).toLocaleDateString($i18n.locale, {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            }}
          </span>
        </template>
      </i18n-t>
      <ModalOfferRichTextMessage
        v-if="!success"
        class="text-neutral-300"
        :message="
          errorMessage
            ? $t(errorMessage)
            : $t('main.offers.transactionFailedDetails')
        " />
    </div>
    <div v-else>
      <BaseIconPending class="mx-auto mt-6 mb-4 w-36" />
      <div class="mt-4 flex flex-col items-center gap-2">
        <p class="text-neutral-200 font-bold text-center">
          {{ $t("main.modalBuySuccess.inProgress") }}
        </p>
        <p class="text-neutral-300 text-center">
          {{ $t("main.modalBuySuccess.processing") }}
        </p>
      </div>
    </div>
    <div class="mt-4 mb-8 flex justify-center">
      <BaseButtonSubmit
        v-if="success"
        :text="$t('main.modalOfferBegin.goToOffers')"
        data-test="button-offer-success"
        size="long"
        :contextual-styles="true"
        @click="navigateTo(localePath(`/user-portal/offers?table=sent`))" />
      <BaseButtonSubmit
        v-else-if="isProcessing"
        :text="$t('main.modalBuySuccess.goToActivity')"
        data-test="button-offer-pending"
        size="long"
        :contextual-styles="true"
        @click="navigateTo(localePath('/user-portal/activity'))" />
      <BaseButtonSubmit
        v-else
        :text="$t('main.modalOfferBegin.close')"
        data-test="button-offer-failed"
        size="long"
        :contextual-styles="true"
        @click="$emit('close')" />
    </div>
  </div>
</template>
